import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'

import { BefehlFormAuftrag, BefehlFormField } from '../Generic'
import { BetriebsstelleField, BetriebsstellenartField } from '../../form'
import { Befehl27FormData } from './schema'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag27_10FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
}

export default function Auftrag27_10Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag27_10FormProps>) {
  const { t } = useTranslation('ui-befehle')

  const formMethods = useFormContext<Befehl27FormData>()
  const { control, formState, watch } = formMethods
  const { errors } = formState

  const sel27_11Betriebsstellenart = watch('auftrag27_10.feld27_11.art')
  const sel27_12Betriebsstellenart = watch('auftrag27_10.feld27_12.art')

  return (
    <BefehlFormAuftrag
      auftragKennung="27.10"
      fieldName="auftrag27_10.enabled"
      readOnly={true}
      testId={testId}
    >
      <div className="flex items-baseline gap-0.5 flex-wrap">
        <span data-testid={`${testId}-text`}>{t('auftraege.27_10.label')}</span>
      </div>
      <div
        className="flex flex-wrap sm:flex-nowrap items-baseline gap-1"
        data-testid={`${testId}-feld-27-11-outer-div`}
      >
        <BorderedBox
          className="w-full"
          data-testid={`${testId}-feld-27-11-bordered-box`}
          footer={
            <span data-testid={`${testId}-feld-27-11-label`}>
              {' '}
              {t('auftraege.27_10.27_11_label')}
            </span>
          }
        >
          <ZugmeldestelleWrapper
            betriebsstellenartField={
              <BefehlFormField
                name="auftrag27_10.feld27_11.art"
                control={control}
                label={t('auftraege.27_10.betriebsstellenart_label')}
                readOnly={readOnly}
                disabled={false}
                activateEmptyWarning={activateEmptyFieldWarnings}
                useTextDisplayInReadOnly={true}
                data-testid={`${testId}-feld-27-11-art`}
                render={({ field, isDisabled, isReadOnly }) => (
                  <BetriebsstellenartField
                    {...field}
                    label={t('auftraege.27_10.betriebsstellenart_label')}
                    variant="solid"
                    selectClassName="w-full"
                    errors={errors}
                    disabled={isDisabled}
                    readOnly={isReadOnly}
                    data-testid={`${testId}-feld-27-11-art-inner`}
                  />
                )}
              />
            }
            bestriebsstelleField={
              <BefehlFormField
                name="auftrag27_10.feld27_11.name"
                control={control}
                label={t('auftraege.27_10.zugmeldestelle_label')}
                readOnly={readOnly}
                disabled={false}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-feld-27-11-name`}
                render={({ field, isDisabled, isReadOnly }) => (
                  <BetriebsstelleField
                    {...field}
                    label={t('auftraege.27_10.zugmeldestelle_label')}
                    errors={errors}
                    disabled={isDisabled}
                    readOnly={isReadOnly}
                    typ={sel27_11Betriebsstellenart}
                    data-testid={`${testId}-feld-27-11-name-inner`}
                  />
                )}
              />
            }
          />
        </BorderedBox>
      </div>
      <div>
        <span data-testid={`${testId}-text-bis`}>
          {t('auftraege.27_10.bis')}
        </span>
      </div>
      <div
        className="flex flex-wrap sm:flex-nowrap items-baseline gap-1"
        data-testid={`${testId}-feld-27-12-outer-div`}
      >
        <BorderedBox
          className="w-full"
          data-testid={`${testId}-feld-27-12-bordered-box`}
          footer={
            <span data-testid={`${testId}-feld-27-12-label`}>
              {t('auftraege.27_10.27_12_label')}
            </span>
          }
        >
          <ZugmeldestelleWrapper
            betriebsstellenartField={
              <BefehlFormField
                name="auftrag27_10.feld27_12.art"
                control={control}
                label={t('auftraege.27_10.betriebsstellenart_label')}
                readOnly={readOnly}
                disabled={false}
                activateEmptyWarning={activateEmptyFieldWarnings}
                useTextDisplayInReadOnly={true}
                data-testid={`${testId}-feld-27-12-art`}
                render={({ field, isDisabled, isReadOnly }) => (
                  <BetriebsstellenartField
                    {...field}
                    label={t('auftraege.27_10.betriebsstellenart_label')}
                    variant="solid"
                    errors={errors}
                    selectClassName="w-full"
                    disabled={isDisabled}
                    readOnly={isReadOnly}
                    data-testid={`${testId}-feld-27-12-art-inner`}
                  />
                )}
              />
            }
            bestriebsstelleField={
              <BefehlFormField
                name="auftrag27_10.feld27_12.name"
                control={control}
                label={t('auftraege.27_10.zugmeldestelle_label')}
                readOnly={readOnly}
                disabled={false}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-feld-27-12-name`}
                render={({ field, isDisabled, isReadOnly }) => (
                  <BetriebsstelleField
                    {...field}
                    label={t('auftraege.27_10.zugmeldestelle_label')}
                    errors={errors}
                    disabled={isDisabled}
                    readOnly={isReadOnly}
                    typ={sel27_12Betriebsstellenart}
                    data-testid={`${testId}-feld-27-12-name-inner`}
                  />
                )}
              />
            }
          />
        </BorderedBox>
      </div>
      <div>
        <span data-testid={`${testId}-text-abschalten`}>
          {t('auftraege.27_10.abschalten')}
        </span>
      </div>
    </BefehlFormAuftrag>
  )
}
